<template>
  <div class="customer-brand-portal-tab">
    <div class="sub-actions">
      <el-button type="primary" size="small" :disabled="!customerId || editDisabled" @click="openDialog()">
        新增品牌门户
      </el-button>
      <el-button size="small" :disabled="!customerId" :loading="loading" @click="loadRows">刷新</el-button>
    </div>
    <el-table :data="rows" border size="small" max-height="360" v-loading="loading">
      <el-table-column label="品牌" min-width="140">
        <template #default="{ row }">
          {{ row.brand_name || row.brand_id }}
        </template>
      </el-table-column>
      <el-table-column prop="fg_vendor_id" label="FG Vendor ID" width="120" show-overflow-tooltip />
      <el-table-column prop="fg_vendor_name" label="FG Vendor" min-width="140" show-overflow-tooltip />
      <el-table-column prop="maker_id" label="Maker ID" width="120" show-overflow-tooltip />
      <el-table-column prop="maker_name" label="Maker Name" min-width="140" show-overflow-tooltip />
      <el-table-column v-if="!editDisabled" label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDialog(row)">编辑</el-button>
          <el-button link type="danger" @click="removeRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="dialogVisible"
      :title="form.id ? '编辑品牌门户' : '新增品牌门户'"
      width="520px"
      append-to-body
    >
      <el-form label-width="130px">
        <el-form-item label="品牌" required>
          <el-select v-model="form.brand_id" filterable style="width: 100%" :disabled="!!form.id">
            <el-option
              v-for="b in brandOptions"
              :key="b.brand_id"
              :label="`${b.brand_code || ''} ${b.brand_name || ''}`"
              :value="b.brand_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="FG Vendor ID">
          <el-input v-model="form.fg_vendor_id" />
        </el-form-item>
        <el-form-item label="FG Vendor">
          <el-input v-model="form.fg_vendor_name" placeholder="品牌系统中的客户名称" />
        </el-form-item>
        <el-form-item label="Maker ID">
          <el-input v-model="form.maker_id" placeholder="第二身份代码，可与 FG ID 相同" />
        </el-form-item>
        <el-form-item label="Maker Name">
          <el-input v-model="form.maker_name" placeholder="第二套名称，可与 FG Vendor 相同" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveRow">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  listBrandSeasonCustomerPortals,
  saveBrandSeasonCustomerPortal,
  deleteBrandSeasonCustomerPortal,
} from '@/api/reports'
import { getBrands } from '@/api/brands'

const props = defineProps({
  customerId: { type: [Number, String], default: null },
  editDisabled: { type: Boolean, default: false },
})

const loading = ref(false)
const saving = ref(false)
const rows = ref([])
const brandOptions = ref([])
const dialogVisible = ref(false)
const form = reactive({
  id: null,
  brand_id: null,
  fg_vendor_id: '',
  fg_vendor_name: '',
  maker_id: '',
  maker_name: '',
})

const loadBrands = async () => {
  try {
    const res = await getBrands({ per_page: 2000, is_active: true })
    brandOptions.value = res?.data?.items || res?.data || res?.items || []
    if (!Array.isArray(brandOptions.value)) brandOptions.value = []
  } catch (e) {
    console.warn(e)
  }
}

const loadRows = async () => {
  if (!props.customerId) {
    rows.value = []
    return
  }
  loading.value = true
  try {
    const res = await listBrandSeasonCustomerPortals({ customer_id: props.customerId })
    rows.value = res?.data || []
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  } finally {
    loading.value = false
  }
}

const openDialog = (row = null) => {
  if (row) {
    Object.assign(form, {
      id: row.id,
      brand_id: row.brand_id,
      fg_vendor_id: row.fg_vendor_id || '',
      fg_vendor_name: row.fg_vendor_name || '',
      maker_id: row.maker_id || '',
      maker_name: row.maker_name || '',
    })
  } else {
    Object.assign(form, {
      id: null,
      brand_id: null,
      fg_vendor_id: '',
      fg_vendor_name: '',
      maker_id: '',
      maker_name: '',
    })
  }
  dialogVisible.value = true
}

const saveRow = async () => {
  if (!props.customerId || !form.brand_id) {
    ElMessage.warning('请选择品牌')
    return
  }
  saving.value = true
  try {
    await saveBrandSeasonCustomerPortal({
      customer_id: props.customerId,
      brand_id: form.brand_id,
      fg_vendor_id: form.fg_vendor_id,
      fg_vendor_name: form.fg_vendor_name,
      maker_id: form.maker_id,
      maker_name: form.maker_name,
    })
    ElMessage.success('已保存')
    dialogVisible.value = false
    loadRows()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    saving.value = false
  }
}

const removeRow = async (row) => {
  try {
    await ElMessageBox.confirm('确定删除该品牌门户资料？', '确认', { type: 'warning' })
    await deleteBrandSeasonCustomerPortal(row.id)
    ElMessage.success('已删除')
    loadRows()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e.response?.data?.message || e.message || '删除失败')
  }
}

watch(
  () => props.customerId,
  () => loadRows(),
  { immediate: true }
)

onMounted(loadBrands)
</script>

<style scoped>
.sub-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
</style>
